import Add16 from "@carbon/icons-react/es/add/16";
import OverflowMenuHorizontal24 from "@carbon/icons-react/es/overflow-menu--horizontal/24";
import { Dropdown, Menu, Modal, notification, Spin } from "antd";
import { ColumnProps } from "antd/lib/table";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import {
  AdvanceEnumFilterMaster,
  Button,
  LayoutCell,
  LayoutHeader,
  OneLineText,
  Pagination,
  StandardTable,
  StatusLine,
  TagFilter,
} from "react3l-ui-library";
import InputSearch from "react3l-ui-library/build/components/Input/InputSearch";
import { listService } from "services/page-services/list-service";
import {
  filterService,
  FilterActionEnum,
} from "services/page-services/filter-service";
import { queryStringService } from "services/page-services/query-string-service";
import {
  getAntOrderType,
  tableService,
} from "services/page-services/table-service";
import nameof from "ts-nameof.macro";
import { IdFilter } from "react3l-advanced-filters";
import { Model } from "react3l-common";
import { Observable } from "rxjs";
import {
  DOCUMENT_PERMISSION_DETAIL_ROUTE,
  DOCUMENT_PERMISSION_ROUTE,
} from "config/route-consts";
import { Permission, PermissionFilter } from "models/Permission";
import { permissionRepository } from "repositories/permission-repository";
import { roleRepository } from "repositories/role-repository";
import { authorizationService } from "services/common-services/authorization-service";
import { API_DOCUMENT_PERMISSION_PREFIX } from "config/api-consts";

function PermissionMaster() {
  const [translate] = useTranslation();
  const history = useHistory();

  const { validAction } = authorizationService.useAuthorizedAction(
    DOCUMENT_PERMISSION_ROUTE.split("/")[
      DOCUMENT_PERMISSION_ROUTE.split("/").length - 1
    ],
    API_DOCUMENT_PERMISSION_PREFIX
  );

  const [modelFilter, dispatch] = queryStringService.useQueryString(
    PermissionFilter,
    { skip: 0, take: 10 }
  );

  const {
    value: filter,
    handleChangeSelectFilter,
    handleChangeInputSearch,
    handleChangeAllFilter,
  } = filterService.useFilter(modelFilter, dispatch);

  const { list, count, loadingList, handleResetList } =
    listService.useList(
      permissionRepository.list,
      permissionRepository.count,
      filter,
      dispatch
    );

  const { handleTableChange, handlePagination } = tableService.useTable(
    filter,
    handleChangeAllFilter
  );

  const { handleAction } = listService.useRowSelection(
    permissionRepository.delete,
    null,
    null,
    null,
    null,
    handleResetList
  );

  /* open the delete confirmation popup (custom text for this screen) */
  const openDeleteConfirm = React.useCallback(
    (item: Permission) => {
      Modal.confirm({
        title: translate("permissions.deleteConfirm.title"),
        content: translate("permissions.deleteConfirm.content"),
        cancelText: translate("general.deletes.cancel"),
        okText: "OK",
        okType: "danger",
        onOk() {
          handleAction(item);
        },
      });
    },
    [translate, handleAction]
  );

  /* loading while the usage-check API is in flight */
  const [checkingUsage, setCheckingUsage] = React.useState(false);

  /* Before deleting, ask BE whether this permission group is still in use.
     isUsed === true  -> in use, show BE's error message(s) (bottom-right).
     isUsed === false -> free to delete, open the confirm popup. */
  const handleDeleteItem = React.useCallback(
    (item: Permission) => () => {
      setCheckingUsage(true);
      permissionRepository.checkDocumentPermissionUsage(item.id).subscribe({
        next: (res: any) => {
          setCheckingUsage(false);
          if (res?.isUsed === true) {
            const messages: string[] = [
              ...(Array.isArray(res?.error?.messages) ? res.error.messages : []),
              ...(Array.isArray(res?.generalErrors) ? res.generalErrors : []),
              ...(res?.errors && typeof res.errors === "object"
                ? Object.values(res.errors).map(String)
                : []),
            ].filter(Boolean);
            if (messages.length > 0) {
              messages.forEach((message: string) =>
                notification.error({ message, placement: "bottomRight" })
              );
            } else {
              notification.error({
                message: translate("permissions.deleteConfirm.inUse"),
                placement: "bottomRight",
              });
            }
          } else {
            openDeleteConfirm(item);
          }
        },
        /* HTTP errors are already reported by the global interceptor */
        error: () => {
          setCheckingUsage(false);
        },
      });
    },
    [openDeleteConfirm, translate]
  );

  /* Classification dropdown: 2 fixed FE options.
     "Thư mục" -> "Document", "Tài liệu" -> "File" (business mapping). */
  const typeList = React.useMemo(
    () => [
      { id: 1, name: translate("permissions.folder") }, // Thư mục -> "Document"
      { id: 2, name: translate("permissions.file") }, // Tài liệu -> "File"
    ],
    [translate]
  );

  const getTypeList = React.useCallback(
    () =>
      new Observable<Model[]>((observer) => {
        observer.next(typeList as Model[]);
        observer.complete();
      }),
    [typeList]
  );

  const handleChangeType = React.useCallback(
    (id: number, item: Model) => {
      const code = id === 1 ? "Document" : id === 2 ? "File" : undefined;
      dispatch({
        type: FilterActionEnum.UPDATE,
        payload: {
          typeCode: code ? new IdFilter({ equal: code } as any) : new IdFilter(),
          typeCodeValue: item ?? undefined,
          skip: 0,
        } as any,
      });
    },
    [dispatch]
  );

  /* Navigate to detail page */
  const handleGoCreate = React.useCallback(() => {
    history.push(DOCUMENT_PERMISSION_DETAIL_ROUTE);
  }, [history]);

  const handleGoDetail = React.useCallback(
    (id: number) => () => {
      history.push(`${DOCUMENT_PERMISSION_DETAIL_ROUTE}?id=${id}`);
    },
    [history]
  );

  const menuAction = React.useCallback(
    (id: number, permission: Permission) => (
      <Menu>
        {validAction("update") && (
          <Menu.Item key="1">
            <div
              className="ant-action-menu"
              onClick={handleGoDetail(id)}
            >
              {translate("general.actions.edit")}
            </div>
          </Menu.Item>
        )}
        {validAction("delete") && (
          <Menu.Item key="2" disabled={permission.used}>
            <div
              className="ant-action-menu"
              onClick={!permission.used ? handleDeleteItem(permission) : undefined}
            >
              {translate("general.actions.delete")}
            </div>
          </Menu.Item>
        )}
      </Menu>
    ),
    [handleGoDetail, handleDeleteItem, translate, validAction]
  );

  /* responsive: recompute column widths on window resize (same as role-master) */
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const columns: ColumnProps<Permission>[] = useMemo(
    () => [
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "id"
          );
          return (
            <div>
              <LayoutHeader
                orderType="left"
                title={translate("permissions.systemCode")}
                sortedColumn={sortedColumn}
                isSorter
              />
            </div>
          );
        },
        key: nameof(list[0].id),
        dataIndex: nameof(list[0].id),
        width: 100,
        sorter: true,
        sortOrder: getAntOrderType<Permission, PermissionFilter>(
          filter,
          nameof(list[0].id)
        ),
        ellipsis: true,
        render(id: number) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <span>{id}</span>
            </LayoutCell>
          );
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "code"
          );
          return (
            <div>
              <LayoutHeader
                orderType="left"
                title={translate("permissions.code")}
                sortedColumn={sortedColumn}
                isSorter
              />
            </div>
          );
        },
        key: nameof(list[0].code),
        dataIndex: nameof(list[0].code),
        width: windowWidth < 1800 ? 300 : 500,
        sorter: true,
        sortOrder: getAntOrderType<Permission, PermissionFilter>(
          filter,
          nameof(list[0].code)
        ),
        ellipsis: true,
        render(code: string, record: Permission) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <div
                className="link-text"
                style={{ cursor: "pointer", textDecoration: "none" }}
                onClick={handleGoDetail(record.id)}
              >
                <OneLineText value={code} countCharacters={30} />
              </div>
            </LayoutCell>
          );
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "name"
          );
          return (
            <div>
              <LayoutHeader
                orderType="left"
                title={translate("permissions.name")}
                sortedColumn={sortedColumn}
                isSorter
              />
            </div>
          );
        },
        key: nameof(list[0].name),
        dataIndex: nameof(list[0].name),
        width: windowWidth < 1800 ? 300 : 500,
        sorter: true,
        sortOrder: getAntOrderType<Permission, PermissionFilter>(
          filter,
          nameof(list[0].name)
        ),
        ellipsis: true,
        render(name: string, record: Permission) {
          return (
            <LayoutCell orderType="left" tableSize="md">
              <div
                className="link-text"
                style={{ cursor: "pointer", textDecoration: "none" }}
                onClick={handleGoDetail(record.id)}
              >
                <OneLineText value={name} countCharacters={40} />
              </div>
            </LayoutCell>
          );
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "typeCode"
          );
          return (
            <div>
              <LayoutHeader
                orderType="left"
                title={translate("permissions.classification")}
                sortedColumn={sortedColumn}
              />
            </div>
          );
        },
        key: nameof(list[0].typeCode),
        dataIndex: nameof(list[0].typeCode),
        width: 200,
        ellipsis: true,
        render(typeCode: string) {
          const label =
            typeCode === "Document"
              ? translate("permissions.folder")
              : typeCode === "File"
              ? translate("permissions.file")
              : "";
          return (
            <LayoutCell orderType="left" tableSize="md">
              <span>{label}</span>
            </LayoutCell>
          );
        },
      },
      {
        title: ({ sortColumns }) => {
          const sortedColumn = sortColumns?.find(
            ({ column }) => column.key === "status"
          );
          return (
            <div>
              <LayoutHeader
                orderType="left"
                title={translate("permissions.status")}
                sortedColumn={sortedColumn}
              />
            </div>
          );
        },
        key: nameof(list[0].statusId),
        dataIndex: nameof(list[0].statusId),
        width: 200,
        ellipsis: true,
        render(statusId: number, record: Permission) {
          const statusName = record.statusId === 1
            ? translate("permissions.active")
            : translate("permissions.inactive");
          const statusColor = record.statusId === 1 ? "green" : "red";
          return (
            <LayoutCell orderType="left" tableSize="md">
              <StatusLine value={statusName} color={statusColor} />
            </LayoutCell>
          );
        },
      },
      {
        key: "action",
        dataIndex: nameof(list[0].id),
        fixed: "right",
        width: 80,
        align: "center",
        render(id: number, permission: Permission) {
          return (
            <div className="d-flex justify-content-center button-action-table">
              <Dropdown
                overlay={menuAction(id, permission)}
                trigger={["click"]}
                placement="bottom"
                arrow
              >
                <OverflowMenuHorizontal24
                  className="btn-missions-blue"
                  style={{ cursor: "pointer" }}
                />
              </Dropdown>
            </div>
          );
        },
      },
    ],
    [translate, list, filter, menuAction, handleGoDetail, windowWidth]
  );

  return (
    <Spin spinning={checkingUsage}>
      <div className="page-content page-content-dxg" id="page-content-dxg-id">
        <div className="info-component ml-4 mb-3">
          <span>{translate("general.defaultHeader.account")}</span>
          <span> &gt; </span>
          <span className="info-primary">
            {translate("menu.title.folderPermissionGroup")}
          </span>
        </div>

        <div className="page page-master m-t--lg m-b--xxs">
          <div className="pt-4 pb-1 ml-3">
            <h5 className="font-bold page-title">
              {translate("permissions.masterTitle")}
            </h5>
          </div>

          <div className="page-master__content">
            <div className="page-master__tag-filter p-t-xxs">
              <TagFilter
                value={filter}
                translate={translate}
                keyTranslate={"permissions"}
                handleChangeFilter={handleChangeAllFilter}
                onClear={(value: any) => {
                  return 0;
                }}
              />
            </div>
            <div className="page-master__filter-wrapper d-flex align-items-center justify-content-between px-3">
              <div className="page-master__filter d-flex align-items-center justify-content-start">
                <div className="d-flex align-items-center mr-3">
                  <AdvanceEnumFilterMaster
                    placeHolder={translate("permissions.placeholder.classification")}
                    value={
                      (filter?.typeCode?.equal as any) === "Document"
                        ? 1
                        : (filter?.typeCode?.equal as any) === "File"
                        ? 2
                        : undefined
                    }
                    onChange={handleChangeType}
                    getList={getTypeList}
                    label={translate("permissions.classification")}
                  />
                </div>
                <div className="d-flex align-items-center">
                  <AdvanceEnumFilterMaster
                    placeHolder={translate("permissions.placeholder.status")}
                    value={filter[nameof(list[0].statusId)]["equal"]}
                    onChange={handleChangeSelectFilter({
                      fieldName: "status",
                      fieldType: "equal",
                      classFilter: IdFilter,
                    })}
                    getList={roleRepository.filterListStatus}
                    label={translate("permissions.status")}
                  />
                </div>
              </div>
              <div className="page-master__filter-action-search d-flex align-items-center">
                <InputSearch
                  value={filter?.search}
                  classFilter={PermissionFilter}
                  placeHolder={translate("general.placeholder.search")}
                  onChange={handleChangeInputSearch}
                  animationInput={filter?.search ? false : true}
                />
              </div>
              <div className="page-master__actions d-flex align-items-center justify-content-start">
                {validAction("create") && (
                  <div className="page-master__filter-action d-flex align-items-center">
                    <Button
                      type="primary"
                      className="btn--lg btn-orange-wIcon ml-3"
                      icon={<Add16 />}
                      onClick={handleGoCreate}
                    >
                      {translate("general.actions.create")}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="page-master__content-table">
            <StandardTable
              rowKey={nameof(list[0].id)}
              columns={columns}
              dataSource={list}
              isDragable={true}
              tableSize={"md"}
              onChange={handleTableChange}
              loading={loadingList}
              scroll={{ x: 1000 }}
            />

            <Pagination
              skip={filter?.skip}
              take={filter?.take}
              total={count}
              onChange={handlePagination}
            />
          </div>
        </div>
      </div>
    </Spin>
  );
}

export default PermissionMaster;
